import React, { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { CustomSelect } from "@/components";
import type { Option } from "@/components";
import AlertDialog from "@/utils/ui/AlertDialog/AlertDialog";
import { playlistApi } from "./playlistMock";
import { fetchPlaylists, toPlaylist, Playlist } from "./playlistData";
import type { PlaylistListItem } from "@/database/media/api";
import {
  GetPlaylistFavoritesApi,
  CreatePlaylistFavoritesApi,
  UpdatePlaylistFavoritesApi,
} from "@/database/user/api";
import {
  ChannelMappings,
  isOnSitePlaylist,
  Confirm,
  PlaylistChip,
  FavStar,
  CheckCircle,
} from "./playlistShared";
import ManageSheet from "./ManageSheet";
import PublishSheet from "./PublishSheet";
import ExpandSheet from "./ExpandSheet";
import { useChipDragOrder } from "../PickerSheet";
export interface PlaylistReorderEntry {
  pid: string;
  position: number;
  title?: string;
  thumbnail?: string;
  duration?: number;
  category?: string;
}

export interface PlaylistMembershipEntry {
  id: string;
  type: "primary" | "secondary";
}

export interface PlaylistGuard {
  hasPendingYouTube: () => boolean;
  promptPublish: () => void;
  getReorderedPlaylists: () => PlaylistReorderEntry[];
  getSelectedPlaylists: () => PlaylistMembershipEntry[];
  getRemovedPlaylists: () => { pid: string }[];
  hasChanges: () => boolean;
}

export const MAX_FAVORITE_PLAYLISTS = 3;

export interface AutoYoutubeChannel {
  channelId: string;
  channelName: string;
  playlistIds: string[];
  category?: string;
}

interface Props {
  mediaId?: string;
  mediaTitle?: string;
  mediaThumbnail?: string;
  mediaDuration?: number;
  mediaCategory?: string;
  team?: string;
  savedPlaylists?: PlaylistMembershipEntry[];
  playlistsData?: PlaylistListItem[];
  mediaState?: number;
  onOpenSocialPublish?: () => void;
  guardRef?: React.MutableRefObject<PlaylistGuard | null>;
  hideHeader?: boolean;
  inlineHeading?: React.ReactNode;
  required?: boolean;
  error?: boolean;
  onSelectionChange?: (count: number) => void;

  onYoutubeChannelsChange?: (channels: AutoYoutubeChannel[]) => void;
  priorityChannelNames?: string[];
  denmarkEnabled?: boolean;
  onDenmarkSectionsChange?: (
    sections: {
      section: number;
      channelName: string;
      host?: number;
      folderPath?: string;
    }[]
  ) => void;
  onDirty?: () => void;
}

const PlaylistPublish: React.FC<Props> = ({
  mediaId = "current",
  mediaTitle = "",
  mediaThumbnail,
  mediaDuration,
  mediaCategory,
  team,
  savedPlaylists,
  playlistsData,
  mediaState = 0,
  onOpenSocialPublish,
  guardRef,
  hideHeader,
  inlineHeading,
  required,
  error,
  onSelectionChange,
  onYoutubeChannelsChange,
  priorityChannelNames = [],
  denmarkEnabled = false,
  onDenmarkSectionsChange,
  onDirty,
}) => {
  const isDraft = (mediaState ?? 0) === 0;

  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [reactivated, setReactivated] = useState<Set<string>>(new Set());
  const [primaryId, setPrimaryId] = useState<string | null>(null); // the one playlist marked primary
  const [loading, setLoading] = useState(true);

  const [siteOnly, setSiteOnly] = useState(false);
  const [expandOpen, setExpandOpen] = useState(false); // channel-browser sheet
  const [expandBtnHovered, setExpandBtnHovered] = useState(false);

  const [confirm, setConfirm] = useState<null | Confirm>(null);
  const [manageId, setManageId] = useState<string | null>(null);
  const [reorderByPlaylist, setReorderByPlaylist] = useState<
    Map<string, PlaylistReorderEntry>
  >(new Map());
  const [publishCh, setPublishCh] = useState<string | null>(null); // YouTube channel whose publish sheet is open
  const [ytPublished, setYtPublished] = useState<Set<string>>(new Set()); // channels already published to YouTube (mock)
  const hasFavPrefRef = useRef(false);

  const savedPlaylistsById = useMemo(() => {
    const rows = Array.isArray(playlistsData) ? playlistsData : [];
    return Object.fromEntries(rows.map((r) => [r.id, toPlaylist(r)]));
  }, [playlistsData]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const favRes = await GetPlaylistFavoritesApi();
      if (!alive) return;
      hasFavPrefRef.current = favRes.status === 200;
      setFavorites(favRes.data);

      const mem = await playlistApi.getMemberships(mediaId);
      if (!alive) return;
      const saved = savedPlaylists ?? [];
      const keys = new Set(saved.length > 0 ? saved.map((p) => p.id) : mem);
      setSelected(keys);
      setInitial(new Set(keys));
      setPrimaryId(saved.find((p) => p.type === "primary")?.id ?? null);
      setYtPublished(new Set());
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const pls = await fetchPlaylists(team);
      if (!alive) return;
      setPlaylists(pls);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [team]);

  // Re-seed the baseline when the saved playlists change on the backend (e.g. after
  // a media Save persists adds/removes). Without this, `initial` stays stale and a
  // subsequent removal would re-send previously-removed playlists in remove_playlists.
  const savedKey = (savedPlaylists ?? [])
    .map((p) => `${p.id}:${p.type}`)
    .sort()
    .join("|");
  const didInitialSeedRef = useRef(false);
  useEffect(() => {
    // Skip the first run — the load effect above already seeds from savedPlaylists.
    if (!didInitialSeedRef.current) {
      didInitialSeedRef.current = true;
      return;
    }
    const saved = savedPlaylists ?? [];
    const keys = new Set(saved.map((p) => p.id));
    setSelected(new Set(keys));
    setInitial(new Set(keys));
    setReactivated(new Set());
    setPrimaryId(saved.find((p) => p.type === "primary")?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  const teamRef = useRef(team);
  teamRef.current = team;
  const refreshPlaylists = async () => {
    const pls = await fetchPlaylists(teamRef.current);
    setPlaylists(pls);
  };

  const byId = useMemo(
    () => ({
      ...savedPlaylistsById,
      ...Object.fromEntries(playlists.map((p) => [p.id, p])),
    }),
    [playlists, savedPlaylistsById]
  );

  const priorityKey = priorityChannelNames.join("|");
  const visible = useMemo(() => {
    const priority = new Set(priorityChannelNames.map((n) => n.toLowerCase()));
    const matched = playlists.filter(
      (p) => !siteOnly || isOnSitePlaylist(p) // no youtube_playlist mapping
    );
    const tier = (p: Playlist) =>
      (p.channelName && priority.has(p.channelName.toLowerCase()) ? 0 : 2) +
      (favorites.includes(p.id) ? 0 : 1);
    return matched
      .map((p, i) => ({ p, i }))
      .sort((a, b) => tier(a.p) - tier(b.p) || a.i - b.i)
      .map(({ p }) => p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlists, favorites, siteOnly, priorityKey]);
  const total = visible.length;

  const effectiveCount = selected.size + reorderByPlaylist.size;
  useEffect(() => {
    onSelectionChange?.(effectiveCount);
  }, [effectiveCount]);

  const toggleSelect = (id: string) => {
    const wasSelected = selected.has(id);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    if (wasSelected && initial.has(id)) {
      setReactivated((prev) => {
        if (prev.has(id)) return prev;
        const next = new Set(prev);
        next.add(id);
        return next;
      });
    }
    if (wasSelected && primaryId === id) setPrimaryId(null);

    if (!isDraft) {
      if (!wasSelected) playlistApi.addToPlaylist(mediaId, id);
    }
  };

  const makePrimary = (id: string) => {
    setPrimaryId((prev) => (prev === id ? null : id));
    if (!selected.has(id)) toggleSelect(id);
    onDirty?.();
  };

  const toggleFav = async (id: string) => {
    const isFav = favorites.includes(id);
    if (!isFav && favorites.length >= MAX_FAVORITE_PLAYLISTS) {
      setConfirm({
        title: "Favorite limit reached",
        confirmText: "OK",
        body: `You can favorite up to ${MAX_FAVORITE_PLAYLISTS} playlists. Remove one to add another.`,
        ok: () => {},
      });
      return;
    }
    const next = isFav ? favorites.filter((f) => f !== id) : [...favorites, id];
    setFavorites(next);
    const save = hasFavPrefRef.current
      ? UpdatePlaylistFavoritesApi(next)
      : CreatePlaylistFavoritesApi(next);
    const res = await save;
    if (res?.error) {
      setFavorites(favorites); // revert on failure
    } else if (!hasFavPrefRef.current) {
      hasFavPrefRef.current = true;
    }
  };

  const applySelection = (next: Set<string>) => {
    if (!isDraft) {
      playlists.forEach((p) => {
        const was = selected.has(p.id);
        const is = next.has(p.id);
        if (!was && is) playlistApi.addToPlaylist(mediaId, p.id);
      });
    }

    setReactivated((prev) => {
      let changed = false;
      const upd = new Set(prev);
      [...selected].forEach((id) => {
        if (!next.has(id) && initial.has(id) && !upd.has(id)) {
          upd.add(id);
          changed = true;
        }
      });
      return changed ? upd : prev;
    });
    setSelected(next);
    if (primaryId && !next.has(primaryId)) setPrimaryId(null);
  };

  const removeMembership = (id: string) =>
    setConfirm({
      title: "Remove from playlist",
      confirmText: "Remove",
      body: "Do you want to remove the video from the playlist?",
      ok: () => {
        setSelected((prev) => {
          const n = new Set(prev);
          n.delete(id);
          return n;
        });
        if (initial.has(id)) {
          setReactivated((prev) => {
            if (prev.has(id)) return prev;
            const n = new Set(prev);
            n.add(id);
            return n;
          });
        }
        if (primaryId === id) setPrimaryId(null);
      },
    });

  const selectedList = [...selected]
    .map((id) => byId[id])
    .filter((p): p is Playlist => !!p);
  const effectivePrimaryId =
    primaryId && selected.has(primaryId)
      ? primaryId
      : (selectedList[0]?.id ?? null);
  const {
    order: chipOrder,
    dragId: chipDragId,
    dragProps: chipDragProps,
    endDropProps: chipEndDropProps,
  } = useChipDragOrder(
    selectedList.map((p) => p.id),
    effectivePrimaryId,
    makePrimary
  );
  const orderedSelectedList = chipOrder
    .map((id) => byId[id])
    .filter((p): p is Playlist => !!p);

  const selectedYtChannels = useMemo<AutoYoutubeChannel[]>(() => {
    const byKey = new Map<string, AutoYoutubeChannel>();
    const seenPlaylists = new Map<string, Set<string>>();
    selectedList.forEach((p) => {
      // A saved playlist that was deselected and reselected is treated as fresh.
      if (initial.has(p.id) && !reactivated.has(p.id)) return;
      (p.youtubeChannels || []).forEach((c) => {
        const key = c.channelId || c.channelName;
        if (!key) return;
        if (!byKey.has(key)) {
          byKey.set(key, {
            channelId: c.channelId || c.channelName,
            channelName: c.channelName,
            playlistIds: [],
            category: c.channelCategory,
          });
          seenPlaylists.set(key, new Set());
        }
        const existing = byKey.get(key)!;
        if (!existing.category && c.channelCategory)
          existing.category = c.channelCategory;
        const seen = seenPlaylists.get(key)!;
        if (c.playlist_id && !seen.has(c.playlist_id)) {
          seen.add(c.playlist_id);
          byKey.get(key)!.playlistIds.push(c.playlist_id);
        }
      });
    });
    return [...byKey.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedList.map((p) => p.id).join("|"),
    playlists,
    initial,
    reactivated,
  ]);

  const channelName = (cid: string) =>
    selectedYtChannels.find((c) => c.channelId === cid)?.channelName || cid;
  const channelPlaylistNames = (cid: string) =>
    selectedList
      .filter((p) =>
        (p.youtubeChannels || []).some(
          (c) => (c.channelId || c.channelName) === cid
        )
      )
      .map((p) => p.name);

  const ytChannels = selectedYtChannels.map((c) => c.channelId);
  const pendingYouTube = isDraft
    ? ytChannels.filter((cid) => !ytPublished.has(cid))
    : [];

  const ytChannelKey = selectedYtChannels
    .map((c) => `${c.channelId}:${c.playlistIds.join(",")}:${c.category ?? ""}`)
    .join("|");
  useEffect(() => {
    onYoutubeChannelsChange?.(selectedYtChannels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytChannelKey, onYoutubeChannelsChange]);

  const selectedKey = [...selected].sort().join("|");
  const initialKey = [...initial].sort().join("|");
  useEffect(() => {
    if (loading) return;
    if (selectedKey !== initialKey) onDirty?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, initialKey, loading]);

  const selectedDenmarkSections = useMemo(() => {
    if (!denmarkEnabled) return [];
    const byKey = new Map<
      number,
      {
        section: number;
        channelName: string;
        host?: number;
        folderPath?: string;
      }
    >();
    selectedList.forEach((p) => {
      if (initial.has(p.id) && !reactivated.has(p.id)) return;
      (p.denmarkChannels || []).forEach((d) => {
        if (d.section == null || byKey.has(d.section)) return;
        byKey.set(d.section, {
          section: d.section,
          channelName: d.channelName,
          host: d.host,
          folderPath: d.folderPath,
        });
      });
    });
    return [...byKey.values()];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedList.map((p) => p.id).join("|"),
    playlists,
    denmarkEnabled,
    initial,
    reactivated,
  ]);

  const denmarkSectionKey = selectedDenmarkSections
    .map((s) => s.section)
    .join("|");
  useEffect(() => {
    onDenmarkSectionsChange?.(selectedDenmarkSections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [denmarkSectionKey]);

  const pendingRef = useRef<string[]>([]);
  useEffect(() => {
    pendingRef.current = pendingYouTube;
  });

  const reorderRef = useRef<PlaylistReorderEntry[]>([]);
  reorderRef.current = [
    ...[...reorderByPlaylist.entries()]
      .filter(([pid]) => selected.has(pid))
      .map(([, entry]) => entry),
    ...[...selected]
      .filter((pid) => !initial.has(pid) && !reorderByPlaylist.has(pid))
      .map((pid) => ({
        pid,
        position: 1,
        title: mediaTitle,
        thumbnail: mediaThumbnail,
        duration: mediaDuration,
        category: mediaCategory,
      })),
  ];
  const membershipRef = useRef<PlaylistMembershipEntry[]>([]);
  membershipRef.current = [...selected].map((id) => ({
    id,
    type: id === primaryId ? "primary" : "secondary",
  }));
  const hasChangesRef = useRef(false);
  hasChangesRef.current =
    selected.size !== initial.size ||
    [...selected].some((id) => !initial.has(id));
  const removedRef = useRef<{ pid: string }[]>([]);
  removedRef.current = [...initial]
    .filter((id) => !selected.has(id))
    .map((id) => ({ pid: id }));

  useEffect(() => {
    if (!guardRef) return;
    guardRef.current = {
      hasPendingYouTube: () => pendingRef.current.length > 0,
      promptPublish: () => setPublishCh(pendingRef.current[0] ?? null),
      getReorderedPlaylists: () => reorderRef.current,
      getSelectedPlaylists: () => membershipRef.current,
      getRemovedPlaylists: () => removedRef.current,
      hasChanges: () => hasChangesRef.current,
    };
    return () => {
      guardRef.current = null;
    };
  }, [guardRef]);

  const openChip = (p: Playlist) => setManageId(p.id);

  const playlistOptions: Option[] = useMemo(
    () =>
      visible.map((p) => {
        const isSel = selected.has(p.id);
        const isFav = favorites.includes(p.id);
        return {
          label: `${p.name} ${p.channelName || ""}`.trim(),
          value: p.id,
          customLabel: (
            <div className="d-flex align-items-start gap-2 w-100">
              <CheckCircle
                filled={isSel}
                className="flex-shrink-0 mt-1"
                title={isSel ? "Remove from selection" : "Add to playlist"}
              />
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <span className="text-truncate d-block">{p.name}</span>
                <ChannelMappings p={p} />
              </div>
              <button
                type="button"
                className={`btn btn-link p-0 flex-shrink-0 playlist-option-fav${
                  isFav ? " is-fav" : ""
                }`}
                title={isFav ? "Remove from favorites" : "Add to favorites"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFav(p.id);
                }}
              >
                <FavStar filled={isFav} />
              </button>
            </div>
          ),
        };
      }),

    [visible, selected, favorites, primaryId]
  );

  const handlePlaylistSelect = (opt: any) => {
    const id = opt?.value;
    if (typeof id === "string") toggleSelect(id);
  };

  return (
    <div className={hideHeader ? undefined : "border rounded mt-3"}>
      {!hideHeader && (
        <div className="d-flex flex-row justify-content-between align-items-center bgGrey">
          <div className="p-2 text-uppercase fw-medium text-primary">
            Add to playlist
          </div>
        </div>
      )}

      <div className={hideHeader ? undefined : "m-2"}>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <span className="d-inline-flex align-items-center gap-1">
            {inlineHeading}
            {required && <span className="text-danger">*</span>}
          </span>
          <label
            className="form-check form-switch d-flex align-items-center gap-2 m-0 small"
            style={{ cursor: "pointer" }}
            title="Show only on-site playlists"
          >
            <span className="text-muted">On site only</span>
            <input
              className="form-check-input m-0"
              type="checkbox"
              role="switch"
              checked={siteOnly}
              onChange={(e) => setSiteOnly(e.target.checked)}
            />
          </label>
        </div>

        <div className="position-relative">
          <CustomSelect
            name="playlists"
            value=""
            placeholder={
              <div className="d-flex align-items-center gap-2">
                <span>Search playlists…</span>
              </div>
            }
            handleChange={handlePlaylistSelect}
            options={playlistOptions}
            updatedOptions={playlistOptions}
            hostSubSection={true}
            customOptionRenderer={(option) => option.customLabel}
            hideArrow={true}
            closeMenuOnSelect={false}
            onMenuOpen={refreshPlaylists}
            customStyling={
              error ? { borderColor: "#dc3545", boxShadow: "none" } : undefined
            }
          />
          <button
            type="button"
            className={`btn p-0 position-absolute d-inline-flex align-items-center justify-content-center border-0 ${
              expandBtnHovered ? "text-dark bg-primary" : "text-secondary"
            }`}
            onMouseEnter={() => setExpandBtnHovered(true)}
            onMouseLeave={() => setExpandBtnHovered(false)}
            style={{
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 1,
              width: 24,
              height: 24,
              borderRadius: 4,
            }}
            title="Browse playlists by channel"
            aria-label="Browse playlists by channel"
            onClick={() => setExpandOpen(true)}
          >
            <i className="bi bi-arrows-angle-expand" style={{ fontSize: 13 }} />
          </button>
        </div>

        {error && (
          <div className="text-danger small mt-1">
            Please add the media to at least one playlist.
          </div>
        )}

        {loading ? (
          <div className="text-muted small py-2 mt-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading playlists…
          </div>
        ) : selectedList.length > 0 ? (
          <div className="d-flex gap-2 mt-3" style={{ flexWrap: "wrap" }}>
            {orderedSelectedList.map((p) => (
              <div
                key={p.id}
                {...chipDragProps(p.id)}
                style={{
                  cursor: "grab",
                  opacity: chipDragId === p.id ? 0.5 : 1,
                  width: "100%",
                }}
              >
                <PlaylistChip
                  name={p.name}
                  primary={p.id === chipOrder[0]}
                  title="Click to manage order"
                  showDragHandle={orderedSelectedList.length > 1}
                  onClick={() => openChip(p)}
                  onRemove={() => removeMembership(p.id)}
                />
              </div>
            ))}
            {/* Tail drop zone — gives room to drag a chip below the last one. */}
            {chipDragId && (
              <div
                style={{ height: 32, width: "100%" }}
                {...chipEndDropProps}
              />
            )}
          </div>
        ) : null}
      </div>

      <ExpandSheet
        show={expandOpen}
        playlists={playlists}
        selected={selected}
        onClose={() => setExpandOpen(false)}
        onApply={applySelection}
        onEdit={setManageId}
        denmarkEnabled={denmarkEnabled}
        favorites={favorites}
        onToggleFav={toggleFav}
        primaryId={primaryId}
        onMakePrimary={makePrimary}
      />

      <ManageSheet
        playlist={manageId ? byId[manageId] : null}
        isNewMembership={manageId ? !initial.has(manageId) : false}
        mediaTitle={mediaTitle}
        mediaId={mediaId}
        mediaThumbnail={mediaThumbnail}
        mediaDuration={mediaDuration}
        mediaCategory={mediaCategory}
        savedPosition={
          manageId ? (reorderByPlaylist.get(manageId)?.position ?? null) : null
        }
        onClose={() => setManageId(null)}
        onConfirm={setConfirm}
        onSaveOrder={(playlistId, entry) => {
          setReorderByPlaylist((prev) => {
            const next = new Map(prev);
            next.set(playlistId, entry);
            return next;
          });
          setSelected((prev) => {
            if (prev.has(playlistId)) return prev;
            const next = new Set(prev);
            next.add(playlistId);
            return next;
          });
          onDirty?.();
        }}
      />

      <PublishSheet
        channelName={publishCh ? channelName(publishCh) : ""}
        targetPlaylists={publishCh ? channelPlaylistNames(publishCh) : []}
        mediaTitle={mediaTitle}
        show={!!publishCh}
        onClose={() => setPublishCh(null)}
        onPublished={() => {
          if (publishCh) setYtPublished((s) => new Set(s).add(publishCh));
        }}
        onOpenSocialPublish={onOpenSocialPublish}
      />

      <AlertDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.ok()}
        title={confirm?.title}
        message={confirm?.body ?? ""}
        confirmText={confirm?.confirmText ?? "Confirm"}
        cancelText="Keep"
      />
    </div>
  );
};

export default PlaylistPublish;
